import React from 'react';
import clsx from 'clsx';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import useFieldApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import { FieldWrapper } from './FieldWrapper';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { isRequiredValidation, dateMask, toDateValue } from '../../utils';
import MaskedInput from 'react-text-mask';
import { Tooltip } from '../Tooltip';
import moment from 'moment';

export interface DatePickerProps extends ReactDatePickerProps {
  name: string;
  wrapperClasses?: string;
  fieldClasses?: string;
  label?: string;
  isError?: boolean;
  error?: string;
  placeholder?: string;
  toolTip?: string;
  asPlaneTextIfDisabled?: boolean;
  clear?: () => void;
  labelClasses?: string;
  fieldLabelClasses?: string;
  errorClassName?: string;
  closeOnScroll?: boolean;
}

export function DatePicker({
  name,
  label = '',
  wrapperClasses,
  fieldClasses,
  isError,
  error,
  onChange,
  selected,
  dateFormat = 'MM/DD/YYYY',
  placeholder,
  placeholderText,
  required,
  disabled,
  toolTip,
  asPlaneTextIfDisabled,
  clear,
  labelClasses,
  fieldLabelClasses,
  errorClassName,
  closeOnScroll = false,
  ...props
}: DatePickerProps) {
  const { t } = useTranslation('form'); // todo move translation to api level
  const CustomInput = React.forwardRef((input, ref) => {
    return (
      <MaskedInput
        id={name}
        mask={dateMask}
        {...input}
        keepCharPositions
        guide
        // @ts-ignore
        ref={ref}
        disabled={disabled}
        aria-describedby={name && `error-for-${name}`}
        aria-invalid={isError}
        aria-required={required}
        data-testid={`date-picker-input${name ? `-${name}` : ''}`}
        className={clsx(
          'truncate form-field react-datepicker_custom',
          isError && 'form-field-error',
          disabled && asPlaneTextIfDisabled && 'border-none bg-transparent pl-0'
        )}
      />
    );
  });

  return (
    <FieldWrapper
      className={wrapperClasses}
      toolTip={toolTip}
      name={name}
      label={t(label)}
      isError={isError}
      error={error}
      labelClasses={labelClasses}
      fieldLabelClasses={fieldLabelClasses}
      errorClassName={errorClassName}
    >
      <div data-tip={selected} data-for={name}>
        <ReactDatePicker
          placeholderText={t(placeholder || (dateFormat as string)?.toUpperCase() || placeholderText || '')}
          selected={selected}
          onChange={onChange}
          wrapperClassName={fieldClasses}
          autoComplete='off'
          closeOnScroll={() => {
            return closeOnScroll;
          }}
          id={name}
          name={name}
          required={required}
          // @ts-ignore
          customInput={<CustomInput />}
          {...props}
        />
      </div>
      {/* eslint-disable-next-line react/jsx-boolean-value */}
      <Tooltip name={name!} isCapture={true} />
    </FieldWrapper>
  );
}

/* istanbul ignore next */ /* this code is not used or will be deleted in other branches */
export function DatePickerDdf(props: any) {
  const {
    input,
    meta: { error, modified }
  } = useFieldApi(props);
  const isError = modified && error && typeof error === 'string';
  const datesOnly = props.datesOnly || false;
  let selectedValue: Date | string = '';
  if (input.value) {
    if (datesOnly) {
      selectedValue = toDateValue(input.value);
    } else {
      selectedValue = parseISO(input.value);
    }
  }

  const isRequired = isRequiredValidation(props.validate);

  const onDateChange = (value: Date) => {
    if (datesOnly) {
      input.onChange(
        moment(value)
          .utc()
          .format(props.dateFormat || 'YYYY-MM-DD')
      );
      return;
    }
    if (value && value.toISOString) {
      input.onChange(value.toISOString());
      return;
    }
    input.onChange('');
  };

  const clearValue = () => {
    input.onChange('');
  };

  return (
    <DatePicker
      {...props}
      isError={isError}
      error={error}
      onChange={onDateChange}
      selected={selectedValue}
      required={isRequired}
      clear={clearValue}
    />
  );
}
